/**
 *
 * Connect GitLab (service account) or GitHub (App) without pasting a token
 *
 */

import React, { useState } from 'react';

import { Box, Text } from '@chakra-ui/react';

import {
  ConnectProvider,
  connectErrorMessage,
  startRepositoryConnect,
} from 'api/onboarding/repositoryConnect';
import { Form } from 'components/DataEntry/Form';
import { FormStyles } from 'components/DataEntry/Form/types';
import {
  autoConnectCopy,
  REPOSITORY_NAME_PATTERN,
} from 'containers/Setup/Integrations/Components/IntegrationsWizard/DataSources/Components/AddRepository/utils';

interface Props {
  provider: ConnectProvider;
  onCancel?();
  styles?: FormStyles;
}

// Rendered with the shared Form (instead of hand-built inputs) so the
// auto-connect view matches the token form's labels, layout and buttons.
export const RepositoryAutoConnect = ({
  provider,
  onCancel,
  styles,
}: Props) => {
  const [name, setName] = useState('');
  const [groupPath, setGroupPath] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const copy = autoConnectCopy[provider];
  const nameError = REPOSITORY_NAME_PATTERN.test(name)
    ? ''
    : 'Only Alphanumeric, hyphen is allowed!';
  const isGitlab = provider === 'gitlab';
  const canSubmit =
    !!name && !nameError && (!isGitlab || !!groupPath.trim()) && !isLoading;

  const onConnect = async () => {
    setError('');
    setIsLoading(true);
    try {
      const url = await startRepositoryConnect(provider, {
        name,
        ...(isGitlab ? { group_path: groupPath.trim() } : {}),
      });
      window.location.assign(url);
    } catch (e) {
      setError(connectErrorMessage(e, copy.fallbackError));
      setIsLoading(false);
    }
  };

  return (
    <Form
      schema={{
        name: {
          type: 'text',
          label: 'Name',
          isRequired: true,
          tooltip: 'Only Alphanumeric, hyphen is allowed.',
          error: nameError,
          value: name,
          onChange: setName,
          autoFocus: true,
        },
        ...(isGitlab
          ? {
              groupPath: {
                type: 'text',
                label: 'GitLab group',
                isRequired: true,
                placeholder: 'acme or acme/platform',
                value: groupPath,
                onChange: setGroupPath,
              },
            }
          : {}),
        note: {
          type: 'custom',
          component: () => (
            <Text fontSize="sm" color="gray.500">
              {copy.note}
            </Text>
          ),
        },
        ...(error
          ? {
              error: {
                type: 'custom',
                component: () => (
                  <Box fontSize="sm" color="critical">
                    {error}
                  </Box>
                ),
              },
            }
          : {}),
      }}
      styles={styles}
      buttonOptions={{
        submit: {
          name: copy.button,
          isLoading,
          isDisabled: !canSubmit,
        },
        reset: {
          name: 'CANCEL',
          isVisible: !!onCancel,
          onClick: () => onCancel?.(),
        },
      }}
      handleSubmit={onConnect}
    />
  );
};
